import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '../api/client';

interface Source {
  id: string;
  name: string;
  sitemap_url: string;
  homepage_url: string;
}

interface SitemapEntry {
  url: string;
  title?: string;
  pubDate?: string;
  source: {
    id: string;
    name: string;
  };
  isNew: boolean;
}

interface ScanResponse {
  total: number;
  new_count: number;
  entries: SitemapEntry[];
  errors?: { source: string; error: string }[];
  scanned_at: string;
}

interface SourcesResponse {
  sources: Source[];
}

interface ImportResponse {
  imported: number;
  skipped: number;
  imported_urls: string[];
}

export function useSitemapSources() {
  return useQuery({
    queryKey: ['sitemaps', 'sources'],
    queryFn: async () => {
      return apiClient.get<SourcesResponse>('/sources/sitemaps');
    },
  });
}

export function useSitemapScan(options?: { source?: string; newOnly?: boolean; limit?: number }) {
  const { source, newOnly = false, limit = 50 } = options || {};

  return useQuery({
    queryKey: ['sitemaps', 'scan', { source, newOnly, limit }],
    queryFn: async () => {
      const params: Record<string, string | number> = { limit };
      if (source) params.source = source;
      if (newOnly) params.new = 'true';

      return apiClient.get<ScanResponse>('/sources/sitemaps/scan', params);
    },
    staleTime: 1000 * 60 * 5, // 5 minutes
  });
}

export function useImportUrls() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (urls: string[]) => {
      return apiClient.post<ImportResponse>('/sources/sitemaps/import', { urls });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['sitemaps'] });
      queryClient.invalidateQueries({ queryKey: ['materials'] });
    },
  });
}
